import { Injectable } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs';
import { CoreImageListModalComponent } from '../core-image-list/core-image-list-modal.component';
import { DataService } from './data.service';
import { attachImageHoverPreview } from '../utils/image-hover-preview.util';

/**
 * Opens the view-only images modal for any entity (asset, fastpm_model,
 * fastpm_model_task, etc.), so callers don't need to wire up
 * CoreImageListModalComponent themselves.
 */
@Injectable()
export class CoreImageViewService {
  private firstImageUrlCache = new Map<string, string | null>();

  constructor(
    private bsModalService: BsModalService,
    private dataService: DataService
  ) {}

  open(entityType: string, entityId: number, title: string): void {
    this.bsModalService.show(CoreImageListModalComponent, {
      class: 'modal-xl',
      initialState: { entityType, entityId, title }
    });
  }

  /**
   * Resolves the URL of an entity's first image, caching the result
   * (including the "no image" case) so repeated hovers over the same
   * entity don't re-request it.
   */
  async getFirstImageUrl(
    entityType: string,
    entityId: number
  ): Promise<string | null> {
    const cacheKey = `${entityType}:${entityId}`;
    if (this.firstImageUrlCache.has(cacheKey)) {
      return this.firstImageUrlCache.get(cacheKey) ?? null;
    }
    try {
      const images = await firstValueFrom(
        this.dataService.get(`api/core/images/${entityType}/${entityId}`)
      );
      const url = images?.[0]?.image_url ?? null;
      this.firstImageUrlCache.set(cacheKey, url);
      return url;
    } catch {
      return null;
    }
  }

  /**
   * Clears the cached first-image URL for an entity. Call this after any
   * mutation (upload/delete) that could change which image is first, so the
   * next hover/eye-icon lookup re-fetches instead of showing stale data.
   */
  invalidateCache(entityType: string, entityId: number): void {
    this.firstImageUrlCache.delete(`${entityType}:${entityId}`);
  }

  /**
   * Appends an eye icon to `container` when `hasImage` is true, otherwise
   * does nothing. Clicking it opens the view-only images modal for the given
   * entity, titled with `entityName` (the entity's own name/description, not
   * a generic label) — also used as the icon's aria-label. Used to show an
   * image indicator beside a name/link cell (e.g. an asset, model, or model
   * task name) in a grid's custom innerRenderer, across different entity
   * types.
   */
  appendEyeIcon(
    container: HTMLElement,
    hasImage: boolean,
    entityType: string,
    entityId: number,
    entityName: string
  ): void {
    if (!hasImage) {
      return;
    }
    const icon = document.createElement('i');
    icon.className = 'la la-eye la-lg text-primary ml-2';
    icon.style.cursor = 'pointer';
    icon.setAttribute('aria-label', entityName);
    icon.onclick = (event) => {
      event.stopPropagation();
      this.open(entityType, entityId, entityName);
    };
    attachImageHoverPreview(icon, () =>
      this.getFirstImageUrl(entityType, entityId)
    );
    container.appendChild(icon);
  }
}
